import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Slider } from './ui/slider';
import TypewriterText from './TypewriterText';
import WeatherContext from './WeatherContext';
import EffortSelector from './EffortSelector';
import FeedbackButtons from './FeedbackButtons';
import TravelPacking from './TravelPacking';
import { getPersonalizedWeatherRecommendations } from '../lib/personalizedWeatherApi';

const StreamingRecommendation = ({ 
  activity, 
  location, 
  coordinates, 
  userPreferences, 
  onRecommendationShown 
}) => {
  const [weatherData, setWeatherData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [effort, setEffort] = useState('EASY');
  const [cyclingSpeed, setCyclingSpeed] = useState(20);
  const [showFeedback, setShowFeedback] = useState(false);

  // Generate natural language recommendation
  const generateRecommendationText = (data) => {
    if (!data) return '';

    const timeOfDay = new Date().getHours() < 12 ? 'morning' : new Date().getHours() < 17 ? 'afternoon' : 'evening';
    const temperature = Math.round(data.temperature?.display || 0);
    const layers = data.recommendation?.layers || 1;
    const emojis = data.recommendation?.emojis || '👕';

    const activityTexts = {
      walk: `For your ${timeOfDay} walk, you'll want ${layers} layer${layers > 1 ? 's' : ''} - ${getLayerDescription(layers, activity)}.`,
      run: `Ready for your run? With your ${effort.toLowerCase()} effort, ${layers} layer${layers > 1 ? 's' : ''} will keep you comfortable - ${getLayerDescription(layers, activity)}.`,
      cycle: `Perfect cycling conditions! At ${cyclingSpeed} km/h, you'll need ${layers} layer${layers > 1 ? 's' : ''} - ${getLayerDescription(layers, activity)}.`,
      travel: `Planning your trip? Here's what to pack based on the 3-day forecast.`
    };

    const greeting = `Good ${timeOfDay}${location ? ` in ${location}` : ''}! It's currently ${temperature}°C outside.`;
    const recommendation = activityTexts[activity] || activityTexts.walk;
    
    return `${greeting}\n\n${recommendation}`;
  };

  const getLayerDescription = (layers, activity) => {
    const descriptions = {
      1: {
        walk: 'a breathable t-shirt will do perfectly',
        run: 'just a moisture-wicking tee - you\'ll warm up quickly',
        cycle: 'a single layer with good ventilation',
        travel: 'light, single-layer clothing'
      },
      2: {
        walk: 'a t-shirt plus a light jacket you can unzip once warm',
        run: 'start with a light layer over your running tee',
        cycle: 'base layer plus a wind-resistant shell for descents',
        travel: 't-shirt and light overshirt'
      },
      3: {
        walk: 'base layer, mid-layer, and outer shell for comfort',
        run: 'thermal base, light mid-layer - shed as you warm up',
        cycle: 'thermal base, insulating mid, windproof outer',
        travel: 'layering system with thermal, sweater, jacket'
      },
      4: {
        walk: 'full layering system with insulation and weather protection',
        run: 'winter running layers - thermal base, fleece, wind shell',
        cycle: 'winter cycling setup with thermal protection',
        travel: 'complete winter wardrobe with heavy insulation'
      },
      5: {
        walk: 'full winter protection - thermal base, insulation, shell, accessories',
        run: 'extreme weather running gear with full coverage',
        cycle: 'winter cycling system with extremity protection',
        travel: 'full winter gear including heavy coat and accessories'
      }
    };

    return descriptions[layers]?.[activity] || descriptions[layers]?.walk || 'appropriate layering for the conditions';
  };

  // Fetch weather data when activity or coordinates change
  useEffect(() => {
    if (!coordinates) return;

    const fetchWeatherData = async () => {
      setIsLoading(true);
      try {
        // Map activity IDs to API values
        const activityMapping = {
          'walk': 'walk',
          'run': 'run', 
          'cycle': 'bike',
          'travel': 'travel'
        };
        
        const apiActivity = activityMapping[activity] || 'walk';
        
        const data = await getPersonalizedWeatherRecommendations({
          latitude: coordinates.latitude,
          longitude: coordinates.longitude,
          activityMode: apiActivity,
          userPreferences: userPreferences || {}
        });
        
        setWeatherData(data);
        if (onRecommendationShown) {
          onRecommendationShown();
        }
      } catch (error) {
        console.error('Failed to fetch weather data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchWeatherData();
  }, [activity, coordinates, effort, cyclingSpeed, userPreferences, onRecommendationShown]);

  if (!coordinates) {
    return (
      <Card className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border-white/20">
        <CardContent className="p-8 text-center">
          <div className="animate-spin h-6 w-6 border-2 border-primary rounded-full border-t-transparent mx-auto mb-4"></div>
          <p className="text-muted-foreground">Getting your location...</p>
        </CardContent>
      </Card>
    );
  }

  if (isLoading || !weatherData) {
    return (
      <Card className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border-white/20">
        <CardContent className="p-8 text-center">
          <div className="animate-spin h-6 w-6 border-2 border-primary rounded-full border-t-transparent mx-auto mb-4"></div>
          <p className="text-muted-foreground">Analyzing conditions...</p>
        </CardContent>
      </Card>
    );
  }

  const recommendationText = generateRecommendationText(weatherData);

  return (
    <div className="space-y-6">
      {/* Main Recommendation Card */}
      <Card className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border-white/20">
        <CardContent className="p-8">
          {/* Streaming Recommendation Text */}
          <div className="mb-6">
            <TypewriterText 
              text={recommendationText}
              className="text-lg leading-relaxed text-gray-800 dark:text-gray-200"
            />
          </div>

          {/* Layer Visualization */}
          {weatherData.recommendation && (
            <div className="flex items-center justify-center gap-4 mb-6 p-4 bg-primary/5 rounded-lg">
              <div className="text-3xl">
                {weatherData.recommendation.emojis}
              </div>
              <div className="text-center">
                <div className="text-2xl font-semibold text-primary">
                  {weatherData.recommendation.layers} layer{weatherData.recommendation.layers > 1 ? 's' : ''}
                </div>
                <div className="text-sm text-muted-foreground">
                  Feels like {Math.round(weatherData.temperature?.apparent || 0)}°C
                </div>
              </div>
            </div>
          )}

          {/* Activity-Specific Controls */}
          {(activity === 'run' || activity === 'cycle') && (
            <div className="mb-6">
              <EffortSelector
                activity={activity}
                effort={effort}
                onEffortChange={setEffort}
              />
            </div>
          )}

          {activity === 'cycle' && (
            <div className="mb-6">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Cycling Speed: {cyclingSpeed} km/h
                </label>
                <Slider
                  value={[cyclingSpeed]}
                  onValueChange={(value) => setCyclingSpeed(value[0])}
                  min={10}
                  max={30}
                  step={5}
                  className="w-full"
                />
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Weather Context */}
      <WeatherContext weatherData={weatherData} />

      {/* Travel Packing List */}
      {activity === 'travel' && weatherData && (
        <TravelPacking weatherData={weatherData} />
      )}

      {/* Feedback */}
      <FeedbackButtons
        onFeedback={(feedback) => {
          console.log('Feedback:', feedback);
          setShowFeedback(true);
          setTimeout(() => setShowFeedback(false), 3000);
        }}
        show={!showFeedback}
      />
    </div>
  );
};

export default StreamingRecommendation;